import type { SharedDailyStatus } from '@adhd/shared';
import { signedMean } from '@adhd/shared';
import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useAi } from '../lib/ai.js';
import { Link } from 'react-router-dom';

/**
 * How a day went: the figures, and a sentence about them.
 *
 * One component for both places it appears — today's, on Today, and any past
 * day, expanded in the Log. They show the same thing because they ARE the same
 * thing, and two implementations of "how was that day" would drift within a
 * week of each other.
 *
 * The figures are counted from the record and always present. The sentence needs
 * the entry-review setting and is written in the second person here — the share
 * link gets the third-person version of the same judgement, from the same model
 * call, so the two can never disagree about the same afternoon.
 *
 * Absence is always explained. "Nothing was written" and "nothing happened" look
 * identical in a blank space and mean opposite things, and on your own screen
 * the second one is the one you would assume.
 */
export function DaySummary({
  day,
  /** Today's is fetched on mount; a past day waits until it is opened. */
  active = true,
  /**
   * What the surrounding screen already shows, so the same fact is not stated
   * twice ten centimetres apart. The list passes both — it lists the entries
   * and folds the check-ins in below — and Today passes neither.
   */
  shownNearby,
}: {
  day: string;
  active?: boolean;
  shownNearby?: { checkins?: boolean; entries?: boolean };
}): JSX.Element | null {
  const { turnedOff } = useAi();
  const [status, setStatus] = useState<SharedDailyStatus | null>(null);
  const [failed, setFailed] = useState(false);

  /*
   * Two passes: the figures, then the sentence.
   *
   * One request returned both, so the counted half — which is instant — waited
   * on a model call that can take several seconds, and then the whole block
   * appeared at once. A screen that sits blank and then fills in is
   * indistinguishable from a broken one, which is exactly how it read.
   *
   * The first call defers and generates nothing; the second is what commissions
   * the writing. Splitting it that way rather than generating in the background
   * means two requests can never produce two different reads of the same day.
   */
  /*
   * `status` is deliberately NOT a dependency, and leaving it in was a bug that
   * hung the screen forever.
   *
   * It was there as a "already loaded, do not refetch" guard, which works fine
   * for one request. With two it is fatal: stage one calls setStatus, the deps
   * change, React runs this effect's CLEANUP, `cancelled` flips to true, and
   * the sentence stage one just asked for is thrown away when it arrives. The
   * spinner then span until the page was reloaded.
   *
   * Keyed on the day instead. It reruns when the fold opens or the day changes,
   * which are the only two moments a fetch is actually wanted, and nothing that
   * happens mid-flight can cancel the request that is still in flight.
   */
  useEffect(() => {
    if (!active || failed) return;
    let cancelled = false;
    api
      .daySummary(day, true)
      .then((first) => {
        if (cancelled) return;
        setStatus(first);
        if (first.analysis !== 'pending') return;
        /*
         * Only the SENTENCE is at stake from here.
         *
         * A failure in this second call used to fall through to the shared
         * catch, which sets `failed` — and `failed` is checked before `status`,
         * so a model that could not be reached threw away the figures already
         * on screen and replaced the whole day with an error. The counted half
         * had arrived and was correct; nothing about it depends on the model.
         *
         * So this one is caught on its own and downgraded to 'unavailable',
         * which is the state the app already has for "the figures are here and
         * nobody wrote the line".
         */
        return api
          .daySummary(day)
          .then((full) => {
            if (!cancelled) setStatus(full);
          })
          .catch(() => {
            if (!cancelled) setStatus({ ...first, analysis: 'unavailable' });
          });
      })
      // Reached only when the FIRST call fails, which means there are no
      // figures either and there is genuinely nothing to show.
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [active, day, failed]);


  if (!active) return null;

  if (failed) {
    return <p className="faint">Couldn't work out how this day went.</p>;
  }

  if (!status) {
    return (
      <p className="faint">
        <span className="spinner" style={{ display: 'inline-block', verticalAlign: -4 }} /> Adding
        it up…
      </p>
    );
  }

  /* Shown in place of the sentence while the model is writing it, with every
     figure already on screen beside it. Says what is happening rather than
     spinning anonymously: the wait is a model reading the day, and several
     seconds of unexplained blank is what made this feel broken. */
  const thinking = status.analysis === 'pending';

  const s = status.stats;

  /*
   * Only what there is. A row of zeroes says how much you opened the app rather
   * than anything about the day, and showing the two the same way is how a quiet
   * Tuesday comes to look like a worrying one.
   */
  /*
   * `to` turns a fact into a door.
   *
   * Only the two facts that count things you WROTE get one. "Overwhelm 3 out of
   * 5" has nowhere to go — it is an average, not a set of objects — and a link
   * on it would promise a screen that does not exist. "Help now, 2 times"
   * deliberately gets none either: those moments are not a list to browse.
   *
   * The destination is the Log at this day rather than the item itself, even
   * when the count is one. Going straight to a single entry would need its id,
   * which means another request on the screen this fold opens on, for a
   * saving of one tap; and the day in the Log is the honest answer to "show me
   * the things this line is counting" whether there is one of them or nine.
   */
  const facts: { label: string; value: string; to?: string; hideIf?: 'checkins' | 'entries' }[] =
    [];
  if (s.checkins > 0) {
    facts.push({
      hideIf: 'checkins',
      label: `Mood${s.lastCheckinAt ? `, last at ${s.lastCheckinAt}` : ''}`,
      value:
        s.meanMood === null
          ? `${s.checkins} check-in${s.checkins === 1 ? '' : 's'}`
          : `${signed(s.meanMood)} average` +
            (s.lowestMood !== null && s.highestMood !== null && s.lowestMood !== s.highestMood
              ? ` · ${signed(s.lowestMood)} to ${signed(s.highestMood)}`
              : '') +
            ` · ${s.checkins} check-in${s.checkins === 1 ? '' : 's'}`,
    });
  }
  if (s.meanOverwhelm !== null) {
    facts.push({ label: 'Overwhelm', value: `${s.meanOverwhelm} out of 5 on average` });
  }
  if (s.sosEvents > 0) {
    facts.push({
      label: 'Help now',
      value:
        `${s.sosEvents} time${s.sosEvents === 1 ? '' : 's'}` +
        (s.sosRelief !== null && s.sosRelief > 0
          ? ` · distress down ${s.sosRelief} on average after`
          : ''),
    });
  }
  if (s.thoughtRecords > 0) {
    facts.push({
      hideIf: 'entries',
      label: 'Thought records',
      to: `/history#day-${day}`,
      value: `${s.thoughtRecords} started${
        s.thoughtRecordsCompleted > 0 ? `, ${s.thoughtRecordsCompleted} worked through` : ''
      }`,
    });
  }
  if (s.journalEntries > 0) {
    facts.push({
      hideIf: 'entries',
      label: 'Journal',
      to: `/history#day-${day}`,
      value: `${s.journalEntries} entr${s.journalEntries === 1 ? 'y' : 'ies'}`,
    });
  }
  /*
   * No medication row here.
   *
   * Both places this appears already show the doses themselves — the Log fold
   * puts the editable cards directly underneath, and Today has them at the top
   * of the screen. A count immediately above the thing it counts is noise, and
   * it was worse than noise: this row reads meds_taken_scheduled, which is
   * bounded by the day's slots, while the line that used to sit at the foot of
   * the Log added the unscheduled ones too. The same day showed "2 of 2" here
   * and "3 doses taken · 2 scheduled" four inches below.
   */

  /*
   * Emptiness is judged on the UNSUPPRESSED list.
   *
   * Otherwise a day whose only content was a journal entry suppresses its one
   * fact and reports "Nothing logged on this day" — directly above the entry it
   * is describing. Suppression is about not repeating the screen, never about
   * what the day contained.
   */
  const shown = facts.filter((f) => !(f.hideIf && shownNearby?.[f.hideIf]));


  /*
   * An empty day still gets the control.
   *
   * This branch fires whenever nothing was logged AND no sentence was written —
   * which with entry review off is EVERY empty day, today's included, because
   * `analysis` is then permanently 'disabled'. Returning a bare line here left
   * the one always-available way to mark something unreachable on exactly the
   * days it exists for: the day it happens, before anything has been logged, is
   * the emptiest day there is.
   */
  if (facts.length === 0 && status.analysis !== 'ready' && !thinking) {
    return (
      <div className="day-summary stack-sm stack">
        <p className="faint">Nothing logged on this day.</p>
      </div>
    );
  }

  return (
    <div className="day-summary stack-sm stack">
      {/* No thread block here any more (14 Oct 2026): a thread is drawn once,
          as a chip above the day, and its controls live on its own page. The
          fold is the reading — the sentence and the figures — and nothing in
          it is a control. */}

      {thinking && (
        <p className="day-summary-thinking">
          <span className="spinner" style={{ display: 'inline-block', verticalAlign: -4 }} />{' '}
          Reading how the day went…
        </p>
      )}

      {status.analysis === 'ready' && status.headlineSelf && (
        <p className="day-summary-line">{status.headlineSelf}</p>
      )}

      {/* Directly under the sentence: both come from the model, so turning
          entry review off cuts one contiguous block rather than leaving two
          holes with counted figures stranded between them. */}
      {status.themes.length > 0 && (
        <div className="chips">
          {status.themes.map((t) => (
            <span className="chip" key={t} style={{ cursor: 'default' }}>
              {t}
            </span>
          ))}
        </div>
      )}

      {shown.length > 0 && (
        <dl className="sv-facts">
          {shown.map((f) => (
            <div key={f.label}>
              <dt>{f.label}</dt>
              {/* The link wraps the value, not the row: the label is what the
                  fact is about and the value is the thing there are some of.
                  Only ever reached in this fold — the Log passes
                  `shownNearby.entries`, which drops both linked facts before
                  they render, so this can never link the Log to itself. */}
              <dd>
                {f.to ? (
                  /* `linkish`, the app's own inline-link idiom. A bare <Link>
                     here rendered in browser-default blue with a default
                     underline — measured at rgb(0,0,238) — because nothing in
                     this stylesheet styles a bare anchor: every link in the app
                     carries a class, so an unclassed one falls all the way
                     through to the user-agent sheet. */
                  <Link className="linkish" to={f.to}>
                    {f.value}
                  </Link>
                ) : (
                  f.value
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {/* Only when the day really was empty — `facts`, not `shown`.
          Testing the filtered list here put "Nothing logged" above a visible
          journal entry, because suppressing the rows the Log already shows made
          the list empty on a day that plainly was not. Suppression is about not
          repeating the screen; it can never be evidence about the day. */}
      {facts.length === 0 && <p className="muted">Nothing logged.</p>}

      {status.trend && s.checkins > 0 && (
        <p className="faint">
          {status.trend === 'worse' && 'Lower than your average for the last two weeks.'}
          {status.trend === 'better' && 'Higher than your average for the last two weeks.'}
          {status.trend === 'similar' && 'About the same as your average for the last two weeks.'}
        </p>
      )}



      {/* Why there is no sentence. On your own screen this matters more than on
          a share link: you are the one who can change it, and a blank space
          where a summary might have been reads as "nothing to say about you". */}
      {status.analysis === 'disabled' && (
        <p className="faint">
          {/*
            "A read" is what the code calls it — readDailyStatus, the read — and
            it had leaked out here as if it were English. The word this app uses
            in front of a person is "summary", and it was sitting one line below
            in the `unavailable` branch the whole time.

            "Turn it on" was wrong as well: this fires at every rung below
            `reflect`, so at `transcribe` and `summarise` the AI is already on
            and the thing that needs changing is how far up it goes.

            The tab travels in router state rather than the URL, so Settings
            still opens on Account when reached any other way and there is no
            back-button trap — the objection its own comment raises.
          */}
          {/*
            What he would get, rather than what is missing. A blank space where
            a summary might have been already reads as "nothing to say about
            you"; naming the absence a second time does not help, and naming the
            gain is at least a reason to act.

            Two versions, because this fires at every rung below `reflect` and
            only one of them is "off". At `transcribe` and `summarise` the AI is
            already running, so "turn on AI" would be telling him to do a thing
            he has already done.
          */}
          {turnedOff
            ? 'AI can write a summary of this day and what stands out in it.'
            : 'A higher AI setting adds a summary of this day and what stands out in it.'}{' '}
          {/* `.linkish`, or it keeps the browser's own link styling — underlined
              in link blue, next to a line of muted grey and the app's green
              everywhere else. Settings.tsx already learned this about the one
              anchor in its chip row. */}
          <Link className="linkish" to="/settings" state={{ tab: 'ai' }}>
            {/* The verb belongs here, not in the sentence as well — "Turn on
                AI … Turn it on" said it twice. */}
            {turnedOff ? 'Turn on AI' : 'Change it'}
          </Link>
        </p>
      )}
      {status.analysis === 'unavailable' && (
        <p className="faint">
          {/* "Not reachable" named one cause and it is usually the wrong one.
              This state also covers the model answering with something that did
              not fit, being rate limited, or stopping before it finished — none
              of which are the network, and all of which look identical from
              here. It is also retried on the next load, which is the part worth
              knowing, and the old sentence did not say it. */}
          No written summary this time. It will try again when you next open this
          day — the figures above are unaffected.
        </p>
      )}

    </div>
  );
}

/** Mood runs −5 to +5, so the sign carries the meaning and has to be shown. */
/* One definition, in @adhd/shared — four copies disagreed, and this was
   the one that rounded nothing. */
const signed = signedMean;
